import { useState } from 'react';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { guardUnsavedBack } from '../../../lib/formGuard';
import type { Attending } from '../../../types/prf';

interface AttendingPerson {
  id: string;
  name: string;
  dealtWithPatient: boolean;
}

function parseAttendingState(data: Attending) {
  try {
    if (data.additionalNotes?.trim().startsWith('{')) {
      const parsed = JSON.parse(data.additionalNotes) as {
        attendingPersons?: AttendingPerson[];
        didDrTreatAssess?: boolean;
        drName?: string;
        drPin?: string;
      };
      return {
        attendingPersons: parsed.attendingPersons?.length
          ? parsed.attendingPersons
          : [{ id: '1', name: data.primaryClinician || '', dealtWithPatient: false }],
        didDrTreatAssess: parsed.didDrTreatAssess ?? false,
        drName: parsed.drName ?? '',
        drPin: parsed.drPin ?? '',
      };
    }
  } catch {
    /* ignore */
  }
  return {
    attendingPersons: [{ id: '1', name: data.primaryClinician || '', dealtWithPatient: false }],
    didDrTreatAssess: false,
    drName: '',
    drPin: '',
  };
}

interface AttendingSectionProps {
  data: Attending;
  onSave: (data: Attending) => void;
  onBack: () => void;
}

export default function AttendingSection({
  data,
  onSave,
  onBack,
}: AttendingSectionProps) {
  const initial = parseAttendingState(data);
  const [attendingPersons, setAttendingPersons] = useState<AttendingPerson[]>(initial.attendingPersons);
  const [didDrTreatAssess, setDidDrTreatAssess] = useState(initial.didDrTreatAssess);
  const [drName, setDrName] = useState(initial.drName);
  const [drPin, setDrPin] = useState(initial.drPin);

  const addAttendingPerson = () => {
    const newId = (attendingPersons.length + 1).toString();
    setAttendingPersons([...attendingPersons, { id: newId, name: '', dealtWithPatient: false }]);
  };

  const removeAttendingPerson = (id: string) => {
    if (attendingPersons.length > 1) {
      setAttendingPersons(attendingPersons.filter(p => p.id !== id));
    }
  };

  const updateAttendingPerson = (id: string, field: keyof AttendingPerson, value: string | boolean) => {
    setAttendingPersons(attendingPersons.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const handleSave = () => {
    onSave({
      primaryClinician: attendingPersons[0]?.name,
      secondaryClinician: attendingPersons[1]?.name,
      additionalNotes: JSON.stringify({
        attendingPersons,
        didDrTreatAssess,
        drName,
        drPin,
      }),
    });
  };

  const handleBackClick = () => guardUnsavedBack(
    initial,
    { attendingPersons, didDrTreatAssess, drName, drPin },
    onBack,
  );

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackClick}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-foreground">Attending</h2>
              <p className="text-xs text-muted-foreground">Personnel who attended the patient</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Attending Persons - Dynamic List */}
          {attendingPersons.map((person, index) => (
            <div key={person.id} className="space-y-4 p-4 rounded-lg border-2 border-border bg-card/50">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  Attending {index + 1}
                </h3>
                {attendingPersons.length > 1 && (
                  <button
                    onClick={() => removeAttendingPerson(person.id)}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                    title="Remove person"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Name */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={person.name}
                    onChange={(e) => updateAttendingPerson(person.id, 'name', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                    placeholder="Enter name"
                  />
                </div>

                {/* Dealt with Patient Toggle */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Dealt with Patient
                  </label>
                  <button
                    onClick={() => updateAttendingPerson(person.id, 'dealtWithPatient', !person.dealtWithPatient)}
                    className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                      person.dealtWithPatient ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        person.dealtWithPatient ? 'translate-x-9' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="ml-3 text-sm text-foreground">
                    {person.dealtWithPatient ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {/* Add Another Person Button */}
          <button
            onClick={addAttendingPerson}
            className="w-full py-3 rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Another Person
          </button>

          {/* Did Dr Treat / Assess Patient */}
          <div className="p-4 rounded-lg border-2 border-border bg-card/50 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Did Dr Treat / Assess Patient
              </label>
              <button
                onClick={() => setDidDrTreatAssess(!didDrTreatAssess)}
                className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                  didDrTreatAssess ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    didDrTreatAssess ? 'translate-x-9' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="ml-3 text-sm text-foreground">
                {didDrTreatAssess ? 'Yes' : 'No'}
              </span>
            </div>

            {/* Dr Name & Dr PIN - Only show if Yes */}
            {didDrTreatAssess && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Dr Name
                  </label>
                  <input
                    type="text"
                    value={drName}
                    onChange={(e) => setDrName(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                    placeholder="Enter doctor name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Dr PIN
                  </label>
                  <input
                    type="text"
                    value={drPin}
                    onChange={(e) => setDrPin(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                    placeholder="Enter PIN"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer with Save Button */}
      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={handleSave}
          className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>
    </div>
  );
}
